import {
  DataTableSource,
  TableChildRow,
  TableRow,
  type DataTablePageOptions,
  type DataTablePageReq,
} from "@carbon6/c6-frontend-design";
import { listDiagnosticJobWindows, type AdminDiagnosticJobWindow } from "@/api/adminDiagnosticJob";
import { adminOperationError } from "@/app/adminOperationError";

// 진단 작업 상세의 구간 표 행 모델이다. 행을 누르면 표가 그 구간의 에피소드를 행 바로 아래 펼친다.
// 펼침 상태는 c6 계약에 따라 행이 가진다. source 는 펼친 구간 uuid 를 기억한다. 그래서 5초 폴링이
// 새 행을 만들어도 source 가 펼침을 유지한다.

export class DiagnosticWindowRow extends TableRow {
  private readonly episodes: readonly DiagnosticWindowEpisodesRow[];

  constructor(
    readonly window: AdminDiagnosticJobWindow,
    private readonly expanded: boolean,
    private readonly onToggle: (row: DiagnosticWindowRow) => void,
  ) {
    super(null);
    this.episodes = [new DiagnosticWindowEpisodesRow(window.uuid, window)];
  }

  // c6 DataTableColumn.key 는 행 프로퍼티만 받는다. 그래서 컬럼 키를 여기서 선언한다.
  get ordinal() {
    return this.window.ordinal;
  }
  get status() {
    return this.window.status;
  }
  // 처리 수는 완료와 실패를 합한 값이다. 건너뛴 에피소드는 완료에 들어 있다.
  get processed() {
    return this.window.completed_count + this.window.failed_count;
  }
  get failed() {
    return this.window.failed_count;
  }
  get worker() {
    return this.window.worker_id;
  }
  get claims() {
    return this.window.claim_count;
  }
  get lastReport() {
    return this.window.last_heartbeat_at;
  }
  get reason() {
    return this.window.reason;
  }
  get isRowClickable() {
    return true;
  }
  onRowClick() {
    this.onToggle(this);
  }
  override get isRowChildrenExpanded() {
    return this.expanded;
  }
  override getRowChildren() {
    return this.episodes;
  }
  withExpanded(expanded: boolean) {
    return new DiagnosticWindowRow(this.window, expanded, this.onToggle);
  }
  getRowId() {
    return this.window.uuid;
  }
  canShow() {
    return true;
  }
  canSelect() {
    return false;
  }
}

// 펼친 구간 아래의 에피소드 영역이다. 내용은 렌더러가 펼칠 때 받는다.
export class DiagnosticWindowEpisodesRow extends TableChildRow {
  constructor(
    parentRowId: string,
    readonly window: AdminDiagnosticJobWindow,
  ) {
    super(parentRowId);
  }
  getRowId() {
    return `${this.getParentRowId()}/episodes`;
  }
  canShow() {
    return true;
  }
  canSelect() {
    return false;
  }
}

export class DiagnosticWindowTableSource extends DataTableSource<DiagnosticWindowRow> {
  // 펼친 구간은 하나뿐이다. 그래서 에피소드 폴링도 요청 하나다.
  private expandedUuid: string | null = null;

  constructor(private readonly jobUuid: string) {
    super({ initialPage: 1, initialPageSize: 20, resolveErrorMessage: adminOperationError });
  }

  protected override async onPage(req: DataTablePageReq, { signal }: DataTablePageOptions) {
    const response = await listDiagnosticJobWindows(this.jobUuid, req, { signal });
    return {
      ...response,
      rows: response.items.map(
        (window) => new DiagnosticWindowRow(window, window.uuid === this.expandedUuid, this.toggle),
      ),
    };
  }

  private readonly toggle = (row: DiagnosticWindowRow) => {
    const rowId = row.getRowId();
    const previous = this.getSnapshot().rows.find(
      (candidate) => candidate.getRowId() === this.expandedUuid && candidate.getRowId() !== rowId,
    );
    const expand = this.expandedUuid !== rowId;
    this.expandedUuid = expand ? rowId : null;
    if (previous !== undefined) this.replaceRow(previous.withExpanded(false));
    this.replaceRow(row.withExpanded(expand));
  };
}
